// Keeping an open tool's data fresh without a manual refresh. Two triggers rerun on.load in the
// background: a change another tool or device saved (POS gets a maxDataChanged message for the
// record types written) and the tool's own refresh interval. Background runs only read: the
// runtime skips writes, toasts, dialogs and navigation, and stops quietly on a failure.
//
// The backend caps background reads separately from the per-minute read limit: per user and
// tool, at most one on.load worth of reads (backgroundReadsPerRun) every BACKGROUND_WINDOW_SECONDS.
// MaxBroker::BACKGROUND_WINDOW and MaxBroker::backgroundReadsPerRun enforce the same numbers.
import { OPERATIONS, RATE_LIMITS } from './operations.ts'
import type { Action, MaxInterfaceSpec } from './interface.ts'

/** refresh is in seconds: at least a minute, so polling stays cheap next to the event-driven reloads */
export const REFRESH_MIN_SECONDS = 60
export const REFRESH_MAX_SECONDS = 3600
/** One background run per tool and user within this many seconds */
export const BACKGROUND_WINDOW_SECONDS: number = RATE_LIMITS.background.windowSeconds
/** Upper bound of the reads one background run may make, whatever on.load holds */
export const MAX_BACKGROUND_READS = 40
/** ±10% random spread on the refresh interval, so tools opened together do not poll together */
export const REFRESH_JITTER = 0.1

/** Read operations that combine many record types; a tool using one reloads after any change */
const ANY_TYPE_READS = ['reports', 'agenda', 'schedule', 'availability', 'export', 'showschedule']

/**
 * Record types a write also changes for readers, on top of its own type: a moved reservation
 * changes resource availability, a paid order its reservations and stock, and so on
 */
export const RELATED_TYPES: Record<string, readonly string[]> = {
  reservations: ['resources', 'resourcepools', 'products', 'orders', 'customers'],
  orders: ['reservations', 'bills', 'customers', 'items', 'products', 'deliveries'],
  bills: ['orders', 'billingcustomers'],
  stock: ['products'],
  products: ['stock'],
  resources: ['reservations', 'resourcepools', 'products'],
  customers: ['orders', 'reservations', 'billingcustomers'],
  billingcustomers: ['bills', 'customers'],
  tags: ['products', 'customers', 'orders'],
  categories: ['products']
}

/** Calls in actions with the then actions they run, skipping catch (a background run stops on a failure instead) */
function eachCall (actions: unknown, visit: (a: Action) => void, withCatch: boolean, depth = 0): void {
  if (!Array.isArray(actions) || depth > 8) return
  for (const a of actions as Action[]) {
    if (!a || typeof a !== 'object') continue
    if (a.do === 'callOperation') {
      visit(a)
      eachCall(a.then, visit, withCatch, depth + 1)
      if (withCatch) eachCall(a.catch, visit, withCatch, depth + 1)
    }
  }
}

function readOperation (spec: MaxInterfaceSpec, a: Action): string | null {
  const ref = spec.operations?.[String(a.operation)]
  return ref && OPERATIONS[ref.operation]?.mode === 'read' ? ref.operation : null
}

/**
 * The reads one background run of on.load may make: its read calls and those in their then
 * actions, at least 1 and at most MAX_BACKGROUND_READS. The backend caps background reads at
 * this many per BACKGROUND_WINDOW_SECONDS.
 */
export function backgroundReadsPerRun (spec: MaxInterfaceSpec): number {
  let n = 0
  eachCall(spec.on?.load, a => { if (readOperation(spec, a)) n++ }, false)
  return Math.min(MAX_BACKGROUND_READS, Math.max(1, n))
}

/**
 * The record types on.load reads, e.g. ['reservations', 'resources']. '*' when a read combines
 * many types (reports, schedules) or names its type with an expression (metadata.get entity).
 */
export function interfaceReadTypes (spec: MaxInterfaceSpec): string[] {
  const types = new Set<string>()
  eachCall(spec.on?.load, a => {
    const operation = readOperation(spec, a)
    if (!operation) return
    if (operation === 'metadata.get') {
      const entity = (a.input as Record<string, unknown> | undefined)?.entity
      types.add(typeof entity === 'string' && entity ? entity : '*')
      return
    }
    const type = operation.split('.')[0]!
    types.add(ANY_TYPE_READS.includes(type) ? '*' : type)
  }, true)
  return [...types].sort()
}

/** The record type a committed write changed: metadata.set names it in entity, others in the operation */
export function writtenType (operation: string, input: Record<string, unknown> | null | undefined): string {
  if (operation === 'metadata.set') return typeof input?.entity === 'string' ? input.entity : ''
  return operation.split('.')[0] ?? ''
}

/** Whether a change to these record types can change what the tool's on.load reads. No types: assume it can. */
export function readsAffectedBy (spec: MaxInterfaceSpec, written: readonly string[] | null | undefined): boolean {
  const reads = interfaceReadTypes(spec)
  if (!reads.length) return false
  if (!written?.length || reads.includes('*')) return true
  return written.some(type => reads.includes(type) || (RELATED_TYPES[type] ?? []).some(t => reads.includes(t)))
}

/** Milliseconds to the next refresh: the interval with ±REFRESH_JITTER spread. random is in [0, 1). */
export function refreshDelay (seconds: number, random: number = Math.random()): number {
  const s = Math.min(REFRESH_MAX_SECONDS, Math.max(REFRESH_MIN_SECONDS, Math.round(seconds) || REFRESH_MIN_SECONDS))
  return Math.round(s * 1000 * (1 - REFRESH_JITTER + 2 * REFRESH_JITTER * random))
}
